import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';

import { EqColors } from '../../../../constants/Eq';
import QATags from '../../../../constants/QATags';
import { createBEM } from '../../../../lib/utils/css';
import { RowItemButtonType } from '../constants';

import RowItemCollectedBadge from './RowItemCollectedBadge';
import RowItemDate from './RowItemDate';
import RowItemEq from './RowItemEq';
import RowItemImage from './RowItemImage';
import RowItemNumber from './RowItemNumber';
import RowItemPlayPauseButton from './RowItemPlayPauseButton';
import RowItemPlusIcon from './RowItemPlusIcon';

import './RowItemLeftColumn.scss';

class RowItemLeftColumn extends React.PureComponent {
  static propTypes = {
    date: PropTypes.instanceOf(Date),
    dominantColor: PropTypes.string,
    hasPlusIcon: PropTypes.bool,
    imgUrl: PropTypes.string,
    isCollected: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isFullRowLink: PropTypes.bool,
    isImgCircle: PropTypes.bool,
    isInteractive: PropTypes.bool,
    isLarge: PropTypes.bool,
    isPaused: PropTypes.bool,
    isPlaying: PropTypes.bool,
    isRowSmall: PropTypes.bool,
    isStation: PropTypes.bool,
    mainText: PropTypes.string,
    mainTextOnclick: PropTypes.func,
    mainTextUrl: PropTypes.string,
    onlyShowRightColPlay: PropTypes.bool,
    pauseButtonOnclick: PropTypes.func,
    playButtonOnclick: PropTypes.func,
    qaImg: PropTypes.oneOf(values(QATags)),
    qaLeftColButton: PropTypes.oneOf(values(QATags)),
    rowNumber: PropTypes.number,
    shouldLinkImg: PropTypes.bool,
    useDarkTheme: PropTypes.bool,
    useLightTheme: PropTypes.bool,
  };

  getImageProps = () => {
    const {
      imgUrl,
      isLarge,
      isImgCircle,
      shouldLinkImg,
      mainText,
      mainTextUrl,
      mainTextOnclick,
      isStation,
      dominantColor,
      qaImg,
      isRowSmall,
    } = this.props;

    return {
      imgUrl,
      isLarge,
      isImgCircle,
      shouldLinkImg,
      mainText,
      mainTextUrl,
      mainTextOnclick,
      isStation,
      dominantColor,
      qaImg,
      isRowSmall,
    };
  };

  getButtonProps = (buttonType, isVisible) => {
    const {
      isDisabled,
      playButtonOnclick,
      pauseButtonOnclick,
      onlyShowRightColPlay,
      mainText,
      qaLeftColButton,
      isInteractive,
      isRowSmall,
      isLarge,
      useDarkTheme,
      useLightTheme,
      isImgCircle,
    } = this.props;

    return {
      buttonType,
      isVisible,
      isDisabled,
      playButtonOnclick,
      pauseButtonOnclick,
      onlyShowRightColPlay,
      isInteractive,
      mainText,
      qaLeftColButton,
      isImgCircle,
      isRowSmall,
      isLarge,
      useDarkTheme,
      useLightTheme,
    };
  };

  getEqProps = ({ isPlaying }) => {
    const { isRowSmall, useDarkTheme, isInteractive, isLarge } = this.props;
    const color = !isRowSmall || (isRowSmall && useDarkTheme) ? EqColors.WHITE : EqColors.BLACK;

    return {
      color,
      isPlaying,
      isInteractive,
      isRowSmall,
      isLarge,
    };
  };

  getBadgeProps = () => {
    const { isCollected, isRowSmall } = this.props;

    return {
      isCollected,
      isRowSmall,
    };
  };

  render = () => {
    const RowItemLeftColumnClasses = createBEM('RowItemLeftColumn', {
      small: this.props.isRowSmall,
    });

    if (this.props.date && !this.props.imgUrl) {
      const { date, isRowSmall, useLightTheme, useDarkTheme } = this.props;

      return (
        <div className={RowItemLeftColumnClasses} data-qa={this.props.qaLeftColButton}>
          <RowItemDate
            date={date}
            isRowSmall={isRowSmall}
            useLightTheme={useLightTheme}
            useDarkTheme={useDarkTheme}
          />
        </div>
      );
    }

    // If isPlaying - show animating EQ / (hover) show pause button
    if (this.props.isPlaying) {
      return (
        <div className={RowItemLeftColumnClasses} data-qa={this.props.qaLeftColButton}>
          <RowItemImage {...this.getImageProps()} />
          <RowItemEq {...this.getEqProps({ isPlaying: true })} />
          <RowItemPlayPauseButton {...this.getButtonProps(RowItemButtonType.PAUSE)} />
          <RowItemCollectedBadge {...this.getBadgeProps()} />
        </div>
      );
    }

    // If isPaused - show non-animating EQ / (hover) show play button
    if (this.props.isPaused) {
      return (
        <div className={RowItemLeftColumnClasses} data-qa={this.props.qaLeftColButton}>
          <RowItemImage {...this.getImageProps()} />
          <RowItemEq {...this.getEqProps({ isPlaying: false })} />
          <RowItemPlayPauseButton {...this.getButtonProps(RowItemButtonType.PLAY)} />
          <RowItemCollectedBadge {...this.getBadgeProps()} />
        </div>
      );
    }

    // If hasPlusIcon - show no button / (hover) show plus icon
    if (this.props.hasPlusIcon) {
      return (
        <div className={RowItemLeftColumnClasses}>
          <RowItemImage {...this.getImageProps()} />
          <RowItemPlusIcon
            isInteractive={this.props.isInteractive}
            isImgCircle={this.props.isImgCircle}
          />
        </div>
      );
    }

    // If isRowSmall && rowNumber - show number / (hover) show play button
    if (this.props.isRowSmall && this.props.rowNumber) {
      return (
        <div className={RowItemLeftColumnClasses} data-qa={this.props.qaLeftColButton}>
          <RowItemNumber
            rowNumber={this.props.rowNumber}
            isInteractive={this.props.isInteractive}
            isFullRowLink={this.props.isFullRowLink}
            useDarkTheme={this.props.useDarkTheme}
          />
          <RowItemPlayPauseButton {...this.getButtonProps(RowItemButtonType.PLAY)} />
        </div>
      );
    }

    // If isRowSmall - show play button / (hover) show play button
    if (this.props.isRowSmall && this.props.playButtonOnclick) {
      return (
        <div className={RowItemLeftColumnClasses} data-qa={this.props.qaLeftColButton}>
          <RowItemPlayPauseButton
            {...this.getButtonProps(RowItemButtonType.PLAY)}
            isVisible={true}
          />
        </div>
      );
    }

    // Default - show no button / (hover) show play button
    if (this.props.imgUrl || this.props.playButtonOnclick || this.props.isCollected) {
      return (
        <div className={RowItemLeftColumnClasses} data-qa={this.props.qaLeftColButton}>
          <RowItemImage {...this.getImageProps()} />
          <RowItemPlayPauseButton {...this.getButtonProps(RowItemButtonType.PLAY)} />
          <RowItemCollectedBadge {...this.getBadgeProps()} />
        </div>
      );
    }

    return null;
  };
}

export default RowItemLeftColumn;
